import { documentTitleOf } from '../documentLink.js';
/**
 * Seat batch CQ-103 — Insights › Feedback & satisfaction: the PURE half of the page (no React, no
 * MSAL) so every rule a manager relies on is pinned without mounting anything.
 *
 * Wire: GET /customers/{id}/insights/feedback/summary and …/items (configClient.js, end of file).
 * The CSAT formula lives on the server (FeedbackInsightsPlanner) — this module only formats it.
 */

/** The ways feedback is received, in the order the page lists them (the server's
 *  FeedbackSourceBuckets, SatoriShared/Auditing/FeedbackDayRollup.cs).  Seat batch CQ-104: human support is
 *  two paths (the post-chat survey · thumbs on agent replies); agent_suggestion / other appear only when the
 *  period holds some.  Seat batch CQ-134 (board row 78): "Thumbs on capability cards" (capability) is listed
 *  again — the result rating ships on both channels (👍 Helpful · 👎 Not helpful at the foot of a lookup card). */
export const SOURCES = [
  'answers', 'conversation', 'form', 'capability', 'support_survey', 'agent_replies', 'citation', 'agent_suggestion', 'other',
];
/** The paths every summary lists (zero rows included) and the "Received through" filter offers — each
 *  has a shipped surface on the web and in Teams (the server's FeedbackSourceBuckets.AlwaysListed, same order). */
export const ALWAYS_LISTED = ['answers', 'conversation', 'form', 'capability', 'support_survey', 'agent_replies', 'citation'];
/** Paths whose rows are not ratings (shown "Not a rating"; outside CSAT). */
export const NOT_RATED = ['citation', 'agent_suggestion'];

/** Lane CQ-238 (operator 2026-10-13: citation problems leave the received table and tiles and "get their own 'Reported problems'
 *  block") — the path counted apart as a reported problem, never as feedback received (the server's FeedbackInsightsPlanner
 *  .IsReportedProblem).  The responses list still shows such a report as a row (its reason, its document). */
export const PROBLEM_SOURCES = Object.freeze(['citation']);
/** Lane CQ-238 — the ways received the "Received through" filter offers: ALWAYS_LISTED less the reported problems (the server's
 *  FeedbackInsightsPlanner.ReceivedListed, same order). */
export const RECEIVED_LISTED = Object.freeze(ALWAYS_LISTED.filter((s) => !PROBLEM_SOURCES.includes(s)));

export const PERIODS = [7, 30, 90];
export const DEFAULT_PERIOD = 30;
export const PAGE_SIZE = 25;
export const UNKNOWN = 'unknown';
export const CHANNELS = ['web', 'teams'];

/** The default filter state of the page. */
export const INITIAL_FILTERS = Object.freeze({
  days: DEFAULT_PERIOD, custom: false, from: '', to: '', country: '', channel: '', source: '', language: '',
});

/** The query the two reads send for a filter state (empty values dropped; a custom range wins
 *  over the preset). */
export function queryOf(filters) {
  const f = filters || INITIAL_FILTERS;
  const custom = Boolean(f.custom || f.from || f.to);
  return {
    ...(custom ? { from: f.from || undefined, to: f.to || undefined } : { days: f.days || DEFAULT_PERIOD }),
    country: f.country || undefined,
    channel: f.channel || undefined,
    source: f.source || undefined,
    language: f.language || undefined,
  };
}

/** True when any narrowing filter (not the period) is set — the empty state offers "Clear filters"
 *  only then (an achievable action, never a dead one). */
export function hasNarrowingFilters(filters) {
  return Boolean(filters && (filters.country || filters.channel || filters.source || filters.language));
}

/** "62.5%" / "—" — a null rate is "no ratings", never 0 %. */
export function formatPct(pct) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return '—';
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

/** "4.3" / "—". */
export function formatStars(avg) {
  if (avg === null || avg === undefined || Number.isNaN(avg)) return '—';
  return avg.toFixed(1);
}

/** The KPI delta for the CSAT tile (percentage points vs the previous period).  A rise is good,
 *  a fall is bad; no comparison ⇒ null (the tile shows no arrow rather than a fake "0"). */
export function csatDelta(csat) {
  const d = csat?.deltaPts;
  if (d === null || d === undefined || Number.isNaN(d)) return null;
  const rounded = Math.round(d * 10) / 10;
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '±';
  return {
    value: `${sign}${Math.abs(rounded).toFixed(1)} pts`,
    dir: rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat',
    tone: rounded > 0 ? 'good' : rounded < 0 ? 'bad' : 'neutral',
  };
}

/** CSAT tile tone — ≥ 80 % good, ≥ 60 % watch, below that a concern; no ratings ⇒ neutral. */
export function csatTone(pct) {
  if (pct === null || pct === undefined) return 'neutral';
  if (pct >= 80) return 'good';
  if (pct >= 60) return 'warn';
  return 'bad';
}

/** Average-stars tile tone — ≥ 4 good, ≥ 3 watch. */
export function starsTone(avg) {
  if (avg === null || avg === undefined) return 'neutral';
  if (avg >= 4) return 'good';
  if (avg >= 3) return 'warn';
  return 'bad';
}

let regionNames = null;
/** "DE" → "Germany"; a free-text value as given; "unknown"/empty → the Unknown label. */
export function countryLabel(code, unknownLabel = 'Unknown') {
  if (!code || code === UNKNOWN) return unknownLabel;
  if (/^[A-Z]{2}$/.test(code)) {
    try {
      regionNames = regionNames || new Intl.DisplayNames(['en'], { type: 'region' });
      return regionNames.of(code) || code;
    } catch { return code; }
  }
  return code;
}

let languageNames = null;
/** "de" → "German"; "zh-cn" → "Chinese (China)"; "unknown" → the Unknown label. */
export function languageLabel(code, unknownLabel = 'Unknown') {
  if (!code || code === UNKNOWN) return unknownLabel;
  try {
    languageNames = languageNames || new Intl.DisplayNames(['en'], { type: 'language' });
    return languageNames.of(code) || code;
  } catch { return code; }
}

/** Sort dimension rows by a column, Unknown ALWAYS last (whatever the direction).  Pure, stable. */
export function sortSlices(rows, key = 'received', dir = 'desc') {
  const list = Array.isArray(rows) ? rows.slice() : [];
  const value = (r) => {
    if (key === 'key') return String(r.key || '').toLowerCase();
    const v = r[key];
    return v === null || v === undefined ? Number.NEGATIVE_INFINITY : v;
  };
  return list
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const au = a.r.key === UNKNOWN ? 1 : 0;
      const bu = b.r.key === UNKNOWN ? 1 : 0;
      if (au !== bu) return au - bu;
      const va = value(a.r);
      const vb = value(b.r);
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return a.i - b.i;
    })
    .map((x) => x.r);
}

/** The sources a summary lists, in display order (the server already decided which appear).  CQ-238 — never a reported-problem
 *  row, even from a server that still sends one (they have their own block). */
export function orderedSources(summary) {
  const rows = Array.isArray(summary?.sources) ? summary.sources : [];
  return SOURCES.filter((k) => !PROBLEM_SOURCES.includes(k)).map((k) => rows.find((r) => r.key === k)).filter(Boolean);
}

/** Lane CQ-238 — a document's title in words, never a raw file name: the console's ONE rule (documentLink.js documentTitleOf — the
 *  folder path and a document extension dropped, underscores read as spaces, a hyphenated slug read as words, the first letter
 *  capitalised; a title already in words stays as written).  '' for nothing.  Pure. */
export function documentWords(title) {
  return documentTitleOf(title == null ? '' : String(title)) ?? '';
}

// Lane CQ-238 — the day headers of the Responses list come from the console's ONE day helper (components/listRows.js groupByDay /
// dayGroupLabel, keys listRows.day.*), shared with every other record list.

/** Lane CQ-238 — the time of day of a row whose day is its group's header ("09:14"), in the console's language.  Pure. */
export function timeOfDay(iso, locale = 'en') {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try { return d.toLocaleTimeString(locale || undefined, { hour: '2-digit', minute: '2-digit' }); } catch { return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); }
}

/** What the "vote" cell shows for a response row. */
export function voteOf(row) {
  // seat batch CQ-148 (board row 146) — a result card's rating carries the thumb AND the stars: both are shown
  if (row?.stars !== null && row?.stars !== undefined && (row?.vote === 'up' || row?.vote === 'down')) {
    return { kind: 'both', vote: row.vote, stars: row.stars, positive: row.vote === 'up' };
  }
  if (row?.stars !== null && row?.stars !== undefined) return { kind: 'stars', stars: row.stars, positive: row.positive };
  if (row?.vote === 'up') return { kind: 'up', positive: true };
  if (row?.vote === 'down') return { kind: 'down', positive: false };
  return { kind: 'none', positive: null };
}

/** The user line of a response row: the named user, else why there is no name (CQ-109: never a hash on the page). */
export function userOf(row) {
  if (row?.identity === 'shown' && row.user) {
    return { kind: 'shown', name: row.user.displayName || row.user.email || '', email: row.user.displayName ? (row.user.email || '') : '', country: row.user.country || row.country || null };
  }
  return { kind: row?.identity || 'not_found' };
}

/** "21–40 of 132" pieces for the pager (and "more than" when the server stopped counting). */
export function pageRange(page) {
  if (!page || !page.total) return { from: 0, to: 0, total: 0, floor: false, pages: 1 };
  const from = (page.page - 1) * page.pageSize + 1;
  const to = Math.min(page.total, from + (page.rows?.length ?? 0) - 1);
  return { from, to, total: page.total, floor: Boolean(page.totalIsFloor), pages: Math.max(1, Math.ceil(page.total / page.pageSize)) };
}

/** The request lifecycle every read on this page follows: idle → loading → ready | error, with a
 *  retry that returns to loading.  Pure reducer so the states are pinned without a DOM. */
export function requestReducer(state, action) {
  switch (action.type) {
    case 'start':   return { status: 'loading', data: state?.data ?? null, error: null, attempt: (state?.attempt ?? 0) + 1 };
    case 'success': return { status: 'ready', data: action.data, error: null, attempt: state?.attempt ?? 1 };
    case 'failure': return { status: 'error', data: null, error: action.error, attempt: state?.attempt ?? 1 };
    default:        return state;
  }
}
export const INITIAL_REQUEST = Object.freeze({ status: 'idle', data: null, error: null, attempt: 0 });

/** The error text a failed read shows: the no-permission line on a 403, the server's problem
 *  detail when it sent one, else the generic line (never a raw stack or status code). */
/** Lane CQ-238 (X7) — a failure in WORDS only: the no-permission line on 403, else the page's own line.  The server's text goes under
 *  Technical details (ErrorWords.ErrorDetail) — Feedback's error cards use this; `errorText` keeps its shape for its other readers. */
export function errorWordsOf(err, fallback, forbidden = fallback) {
  return err?.status === 403 ? forbidden : fallback;
}

export function errorText(err, fallback, forbidden = fallback) {
  if (err?.status === 403) return forbidden;
  return err?.problem?.detail || fallback;
}

/** A compact date + time for a response row ("Oct 6, 09:14"; the year when not this year) — CQ-238: in the console's language when
 *  the caller passes it (English otherwise, as before). */
export function formatAt(iso, now = new Date(), locale = 'en') {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear();
  const opts = { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), hour: '2-digit', minute: '2-digit' };
  try { return d.toLocaleString(locale || undefined, opts); } catch { return d.toLocaleString(undefined, opts); }
}

/** A short axis date for a series point ("Oct 6"). */
export function shortDate(iso) {
  try {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  } catch { return String(iso); }
}
